<script setup>
import { onBeforeUnmount, onMounted, ref } from "vue";
import apiClient from "@/apiClient.js";

const props = defineProps({ returnId: { type: Number, required: true } });
const emit = defineEmits(["cancel", "included"]);
const search = ref("");
const rows = ref([]);
const selectedIds = ref([]);
const page = ref(1);
const lastPage = ref(1);
const total = ref(0);
const loading = ref(false);
const saving = ref(false);
const error = ref("");
let controller = null;

const load = async (nextPage = 1) => {
  controller?.abort();
  const requestController = new AbortController();
  controller = requestController;
  loading.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.get(
      `/api/vat-returns/${props.returnId}/earlier-outgoing-transactions`,
      {
        params: { search: search.value.trim(), page: nextPage },
        signal: requestController.signal,
        suppressErrorAlert: true,
      },
    );
    if (requestController.signal.aborted) return;
    rows.value = data.data;
    page.value = data.current_page;
    lastPage.value = data.last_page;
    total.value = data.total;
  } catch (failure) {
    if (requestController.signal.aborted) return;
    error.value =
      failure?.response?.data?.message ??
      "Unable to load earlier transactions.";
  } finally {
    if (!requestController.signal.aborted) loading.value = false;
  }
};
const include = async () => {
  if (saving.value || !selectedIds.value.length) return;
  saving.value = true;
  error.value = "";
  try {
    const { data } = await apiClient.post(
      `/api/vat-returns/${props.returnId}/earlier-outgoing-transactions`,
      { transaction_ids: selectedIds.value },
      { suppressErrorAlert: true },
    );
    emit("included", data);
  } catch (failure) {
    error.value =
      failure?.response?.data?.message ??
      "Unable to add earlier transactions. Refresh the list and try again.";
  } finally {
    saving.value = false;
  }
};
const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    (value ?? 0) / 100,
  );
onMounted(() => load());
onBeforeUnmount(() => controller?.abort());
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/75 px-4 py-8"
    role="dialog"
    aria-modal="true"
    aria-labelledby="earlier-outgoing-title"
  >
    <div
      class="max-h-full w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 text-slate-900 shadow-2xl dark:bg-slate-800 dark:text-slate-100"
    >
      <div class="flex items-start justify-between gap-4">
        <h2 id="earlier-outgoing-title" class="text-lg font-semibold">
          Add earlier outgoing transactions
        </h2>
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Close
        </button>
      </div>
      <p class="mt-3 text-sm text-slate-600 dark:text-slate-300">
        Select missed payments to include on this return. Their original dates
        are kept. Payments already assigned to a return, reviewed, linked to
        purchase evidence, or present when an earlier return was closed are
        excluded. Check against your filed workbook for returns completed
        outside Neptune.
      </p>
      <form class="mt-4 flex gap-2" @submit.prevent="load(1)">
        <input
          v-model="search"
          type="search"
          aria-label="Search earlier transactions"
          placeholder="Description or supplier"
          class="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        />
        <button class="btn" :disabled="loading || saving">Search</button>
      </form>
      <p
        v-if="error"
        class="mt-3 text-sm text-rose-600 dark:text-rose-300"
        role="alert"
      >
        {{ error }}
      </p>
      <p v-if="loading" class="mt-4 text-sm" role="status">
        Loading earlier transactions…
      </p>
      <template v-else>
        <p class="mt-4 text-sm text-slate-600 dark:text-slate-300">
          {{ total }} eligible · {{ selectedIds.length }} selected
        </p>
        <div class="mt-2 overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead
              class="text-left text-xs uppercase text-slate-500 dark:text-slate-400"
            >
              <tr>
                <th class="p-2">Include</th>
                <th class="p-2">Date</th>
                <th class="p-2">Description</th>
                <th class="p-2">Supplier</th>
                <th class="p-2 text-right">Paid out</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="border-t border-slate-200 dark:border-slate-700"
              >
                <td class="p-2">
                  <input
                    v-model="selectedIds"
                    type="checkbox"
                    :value="row.id"
                    :aria-label="`Include ${row.description}`"
                    :disabled="
                      saving ||
                      (selectedIds.length >= 500 &&
                        !selectedIds.includes(row.id))
                    "
                  />
                </td>
                <td class="whitespace-nowrap p-2">
                  {{ row.transaction_date }}
                </td>
                <td class="p-2">{{ row.description }}</td>
                <td class="p-2">{{ row.supplier?.company || "Not linked" }}</td>
                <td class="whitespace-nowrap p-2 text-right">
                  {{ money(row.paid_out) }}
                </td>
              </tr>
              <tr v-if="!rows.length">
                <td colspan="5" class="p-4 text-slate-500 dark:text-slate-400">
                  No eligible earlier transactions found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="lastPage > 1"
          class="mt-3 flex items-center justify-between gap-2"
        >
          <button
            type="button"
            class="btn"
            :disabled="page <= 1 || saving"
            @click="load(page - 1)"
          >
            Previous
          </button>
          <span class="text-sm">Page {{ page }} of {{ lastPage }}</span>
          <button
            type="button"
            class="btn"
            :disabled="page >= lastPage || saving"
            @click="load(page + 1)"
          >
            Next
          </button>
        </div>
      </template>
      <div class="mt-6 flex justify-end gap-2">
        <button
          type="button"
          class="btn"
          :disabled="saving"
          @click="emit('cancel')"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn accent disabled:opacity-50"
          :disabled="saving || loading || !selectedIds.length"
          @click="include"
        >
          {{ saving ? "Adding…" : `Add ${selectedIds.length} to this return` }}
        </button>
      </div>
    </div>
  </div>
</template>
